import { useState } from 'react';
import Logo from './Logo';
import UserAvatar from './UserAvatar';
import CreateProjectModal from './CreateProjectModal';
import ConfirmModal from './ConfirmModal';
import UserManagementModal from './UserManagementModal';
import { LogOutIcon, PlusIcon, FolderKanbanIcon, LoaderIcon, ChevronRightIcon, DeleteIcon, LayoutGridIcon, ZapIcon, RefreshIcon, SettingsIcon } from './Icons';
import ProjectTypeFilterBar from './shared/ProjectTypeFilterBar';
import useProjectDashboard from '../hooks/useProjectDashboard';

export default function ProjectDashboard({ user, userProfile, users, onSelectProject, onLogout, showToast, refreshUsers }) {
  const {
    projects, filteredProjects, loading, migrating, deleteTarget, setDeleteTarget,
    typeFilter, setTypeFilter, isAdmin, handleCreateProject, handleDeleteProject,
  } = useProjectDashboard({ user, userProfile, showToast });

  const [showCreate, setShowCreate] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);

  const handleCreate = async (data) => {
    try {
      await handleCreateProject(data);
      showToast('Project created', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to create project', 'error');
      throw err;
    }
  };

  if (loading || migrating) {
    return (
      <div className="app-loading">
        <LoaderIcon size={32} />
        <p>{migrating ? 'Migrating data...' : 'Loading projects...'}</p>
      </div>
    );
  }

  return (
    <div className="project-dashboard">
      <header className="project-dashboard-header">
        <Logo size="small" showTagline />
        <div className="project-dashboard-header-right">
          <button className="btn btn-ghost btn-sm header-avatar-btn" title="My Profile">
            <UserAvatar user={userProfile} size={32} showStatus={false} />
          </button>
          {isAdmin && (
            <button className="btn btn-ghost btn-sm" onClick={() => setShowUserManagement(true)} title="Settings">
              <SettingsIcon size={16} />
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={onLogout}>
            <LogOutIcon size={16} /> Logout
          </button>
        </div>
      </header>

      <div className="project-dashboard-body">
        <div className="project-dashboard-title-row">
          <h2>My Projects</h2>
          {isAdmin && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
              <PlusIcon size={16} /> New Project
            </button>
          )}
        </div>

        {projects.length > 0 && (
          <ProjectTypeFilterBar typeFilter={typeFilter} onChange={setTypeFilter} />
        )}

        {projects.length === 0 ? (
          <div className="project-dashboard-empty">
            <FolderKanbanIcon size={48} />
            <h3>No projects yet</h3>
            <p>{isAdmin ? 'Create your first project to get started.' : 'Ask your admin to add you to a project.'}</p>
            {isAdmin && (
              <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                <PlusIcon size={16} /> Create Project
              </button>
            )}
          </div>
        ) : (
          <div className="project-card-grid">
            {filteredProjects.map((project) => {
              const memberUsers = users.filter((u) => project.members?.includes(u.uid));
              const projType = project.type || 'scrum';
              const isKanban = projType === 'kanban';
              const isRetro = projType === 'retrospective';
              return (
                <div
                  key={project.id}
                  className="project-card"
                  onClick={() => onSelectProject(project)}
                >
                  <div className="project-card-top">
                    <div className={`project-card-icon ${isKanban ? 'project-card-icon--kanban' : isRetro ? 'project-card-icon--retro' : ''}`}>
                      {isKanban ? <ZapIcon size={24} /> : isRetro ? <RefreshIcon size={24} /> : <LayoutGridIcon size={24} />}
                    </div>
                    <div className="project-card-info">
                      <h3 className="project-card-name">{project.name}</h3>
                      {project.description && (
                        <p className="project-card-desc">{project.description}</p>
                      )}
                    </div>
                    {isAdmin && (
                      <button
                        className="btn-icon project-card-delete"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(project.id); }}
                        title="Delete project"
                      >
                        <DeleteIcon size={14} />
                      </button>
                    )}
                    <div className="project-card-arrow">
                      <ChevronRightIcon size={18} />
                    </div>
                  </div>
                  <div className="project-card-footer">
                    <div className="project-card-members">
                      {memberUsers.slice(0, 5).map((u) => (
                        <UserAvatar key={u.uid} user={u} size={24} showStatus={false} />
                      ))}
                      {memberUsers.length > 5 && (
                        <span className="project-card-members-more">+{memberUsers.length - 5}</span>
                      )}
                    </div>
                    <div className="project-card-footer-right">
                      <span className={`project-type-chip ${isKanban ? 'project-type-chip--kanban' : isRetro ? 'project-type-chip--retro' : 'project-type-chip--scrum'}`}>
                        {isKanban ? <><ZapIcon size={12} /> Kanban</> : isRetro ? <><RefreshIcon size={12} /> Retro</> : <><LayoutGridIcon size={12} /> Scrum</>}
                      </span>
                      <span className="project-card-member-count">
                        {memberUsers.length} member{memberUsers.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onCreate={handleCreate}
          users={users}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          message="Delete this project and ALL its sprints and items? This cannot be undone."
          onConfirm={handleDeleteProject}
          onCancel={() => setDeleteTarget(null)}
          confirmLabel="Delete Project"
        />
      )}

      {showUserManagement && (
        <UserManagementModal
          users={users}
          onClose={() => setShowUserManagement(false)}
          onUsersChanged={refreshUsers}
          showToast={showToast}
        />
      )}
    </div>
  );
}
